import { unref, type MaybeRef } from 'vue'
import { computedAsync, type AsyncComputedOptions } from '@vueuse/core'
import { getProductComparison, getProductComparisonPrice, getProductComparisonProducts } from '@/api'
import type { ProductComparisonItem, ProductComparisonPriceItem } from '@/types'

export const useProductComparisonAsync = (
    productCategoryKey: MaybeRef<string>,
    options?: AsyncComputedOptions,
) =>
    computedAsync<ProductComparisonItem[]>(
        async (onCancel) => {
            const abortController = new AbortController()

            onCancel(() => abortController.abort())

            const productComparisonData = await getProductComparison(
                unref(productCategoryKey),
                abortController.signal,
            )

            if (!productComparisonData) return []

            return productComparisonData.filter(
                (product) => !product.excludedFromComparison,
            )
        },
        [],
        options,
    )

export const useProductComparisonProductsAsync = (
    productCategoryId: MaybeRef<string>,
    productKeys: MaybeRef<number[]>,
    options?: AsyncComputedOptions,
) =>
    computedAsync<ProductComparisonItem[]>(
        async (onCancel) => {
            const abortController = new AbortController()

            onCancel(() => abortController.abort())

            const keys = unref(productKeys)
            if (!keys || keys.length === 0) {
                return []
            }

            const productComparisonData = await getProductComparisonProducts(
                unref(productCategoryId),
                keys,
                abortController.signal,
            )

            if (!productComparisonData) return []

            return productComparisonData.filter(
                (product) => !product.excludedFromComparison,
            )
        },
        [],
        options,
    )

export const useProductComparisonPriceAsync = (
    productIds: MaybeRef<number[]>,
    options?: AsyncComputedOptions,
) =>
    computedAsync<ProductComparisonPriceItem[]>(
        async (onCancel) => {
            const abortController = new AbortController()

            onCancel(() => abortController.abort())

            const ids = unref(productIds)
            if (!ids || ids.length === 0) {
                return []
            }

            return await getProductComparisonPrice(ids, abortController.signal)
        },
        [],
        options,
    )
